import { Button, Col, DatePicker, Form, Input, Radio, Row, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import type { RentalOrder } from '@super-mega/server/contracts/order'
import { REQUIRED_FIELD_RULES as required } from '@/const/form.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { QuickBusinessSelect } from './quick-business-select.tsx'
import type { RentalFormValues } from './rental-form.types.ts'
import type { RentalRoomRow, RentalSiteRow } from './rental-resource-selector-modals.tsx'
import type { BusinessOption } from './service-order-form.types.ts'
import { formatSiteAddress, rentalResourceLabels } from './utils/rental-address.ts'

export function RentalResourceFields({
  batch = false,
  businessOptions,
  businessLoading,
  canCreateBusiness = false,
  existingOrder,
  selectedSite,
  selectedRoom,
  busy,
  onBusinessSearch,
  onBusinessChange,
  onOccupancyChange,
  onOpenResource
}: {
  batch?: boolean
  businessOptions?: BusinessOption[]
  canCreateBusiness?: boolean
  businessLoading: boolean
  existingOrder?: RentalOrder
  selectedSite?: RentalSiteRow
  selectedRoom?: RentalRoomRow
  busy: boolean
  onBusinessSearch: (keyword: string) => void
  onBusinessChange: (value: number, option?: BusinessOption) => void
  onOccupancyChange: (value: string) => void
  onOpenResource: () => void
}) {
  const form = Form.useFormInstance<RentalFormValues>()
  const occupancyMode = Form.useWatch('occupancyMode', form)
  const businessId = Form.useWatch('businessId', form)
  const labels = rentalResourceLabels({ batch, businessId, occupancyMode, site: selectedSite, room: selectedRoom })
  const addressText = formatSiteAddress(selectedSite, selectedRoom)
  const setPeriodWithDefault = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const start = dates?.[0]
    if (!start) {
      form.setFieldValue('period', undefined)
      return
    }
    if (!dates[1]) {
      form.setFieldValue('period', [start, start.add(1, 'year')])
      return
    }
    form.setFieldValue('period', [start, dates[1]])
  }

  return (
    <section
      className="border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs"
      id="section-rental-resource"
    >
      <div className="mb-5">
        <Typography.Title className="mb-1!" level={5}>
          租赁业务与地址
        </Typography.Title>
        <Typography.Text type="secondary">
          {batch
            ? '先选择业务与使用方式，再选择地址。提交时自动分配不同的空闲房间。'
            : '先选择业务与使用方式，再选择租赁地址。实际入驻需指定房间。'}
        </Typography.Text>
      </div>
      <Row gutter="1.5rem">
        <Col xs={24} md={10}>
          <Form.Item label="租赁业务" name="businessId" rules={required}>
            <QuickBusinessSelect
              businessType="lease"
              canCreate={canCreateBusiness}
              disabled={busy}
              loading={businessLoading}
              options={
                existingOrder?.businessId
                  ? [
                      {
                        value: existingOrder.businessId,
                        label: existingOrder.businessName ?? '租赁业务',
                        referencePrice: existingOrder.referencePrice
                      },
                      ...(businessOptions ?? []).filter((item) => item.value !== existingOrder.businessId)
                    ]
                  : businessOptions
              }
              placeholder="请选择租赁业务"
              showSearch={{ filterOption: false, onSearch: onBusinessSearch }}
              onChange={onBusinessChange}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={7}>
          <Form.Item label="使用方式" name="occupancyMode" rules={required}>
            <Radio.Group
              block
              optionType="button"
              options={[
                { label: '实际入驻', value: 'physical' },
                { label: '注册地址', value: 'registration' }
              ]}
              onChange={(event) => onOccupancyChange(event.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={7}>
          <Form.Item label="办理情形" name="leaseAction" rules={required}>
            <Radio.Group
              block
              optionType="button"
              options={[
                { label: '设立', value: 'setup' },
                { label: '变更', value: 'change' },
                { label: '登记', value: 'registration' }
              ]}
            />
          </Form.Item>
        </Col>
      </Row>
      <div className="border-border border-t pt-4">
        <div className="grid gap-x-6 md:grid-cols-2">
          <Form.Item
            label={labels.label}
            name="siteId"
            dependencies={['occupancyMode', 'roomId']}
            rules={[
              { required: true, message: '请选择租赁地址' },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !batch && value && getFieldValue('occupancyMode') === 'physical' && !getFieldValue('roomId')
                    ? Promise.reject(new Error('实际入驻需选择房间，请点击更换场地或房间'))
                    : Promise.resolve()
              })
            ]}
            extra={labels.hint}
          >
            <Button disabled={busy || !businessId} onClick={onOpenResource}>
              {labels.button}
            </Button>
          </Form.Item>
          <Form.Item label="合同租期" name="period" rules={required}>
            <DatePicker.RangePicker presets={RANGE_PRESETS} className="w-full" onChange={setPeriodWithDefault} />
          </Form.Item>
        </div>
        <Form.Item hidden name="roomId">
          <Input type="hidden" />
        </Form.Item>
        {selectedSite && (
          <div className="bg-surface-muted rounded-lg p-4" aria-live="polite">
            <p className="text-content m-0 font-medium">
              {selectedSite.name}
              {labels.selection}
            </p>
            <p className="text-content-muted mt-2 mb-0 text-sm break-words">{addressText || '未填写详细地址'}</p>
            <p className="text-content-soft mt-1 mb-0 text-sm break-words">
              归属主体：{selectedSite.organization?.name ?? '尚未绑定经营主体，请先完善场地资料。'}
            </p>
          </div>
        )}
      </div>
      <Form.Item name="referencePrice" noStyle>
        <Input type="hidden" />
      </Form.Item>
    </section>
  )
}
